import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { dictionaryService } from '../services/dictionaryService';
import { API_BASE_URL } from '../config';
import './PhonicsReferencePage.css';

interface CategoryInfo {
    key: string;
    letters: [string, string];
    type: 'voicing' | 'liquid' | 'nasal' | 'cross' | 'vowel' | 'visual' | 'spelling';
}

const CATEGORIES: CategoryInfo[] = [
    { key: 'b_p', letters: ['B', 'P'], type: 'voicing' },
    { key: 'd_t', letters: ['D', 'T'], type: 'voicing' },
    { key: 'f_v', letters: ['F', 'V'], type: 'voicing' },
    { key: 's_z', letters: ['S', 'Z'], type: 'voicing' },
    { key: 'k_g', letters: ['K', 'G'], type: 'voicing' },
    { key: 'l_r', letters: ['L', 'R'], type: 'liquid' },
    { key: 'v_w', letters: ['V', 'W'], type: 'liquid' },
    { key: 'm_n', letters: ['M', 'N'], type: 'nasal' },
    { key: 'b_v', letters: ['B', 'V'], type: 'cross' },
    { key: 'l_w', letters: ['L', 'W'], type: 'cross' },
    { key: 'i_e', letters: ['I', 'E'], type: 'vowel' },
    { key: 'a_e', letters: ['A', 'E'], type: 'vowel' },
    { key: 'o_u', letters: ['O', 'U'], type: 'vowel' },
    { key: 'a_u', letters: ['A', 'U'], type: 'vowel' },
    { key: 'visual_bd', letters: ['B', 'D'], type: 'visual' },
    { key: 'visual_pq', letters: ['P', 'Q'], type: 'visual' },
    // Spelling patterns (digraphs)
    { key: 'ee_ea', letters: ['ee', 'ea'], type: 'spelling' },
    { key: 'oo_ou', letters: ['oo', 'ou'], type: 'spelling' },
    { key: 'ai_ay', letters: ['ai', 'ay'], type: 'spelling' },
    { key: 'oi_oy', letters: ['oi', 'oy'], type: 'spelling' },
    { key: 'ou_ow', letters: ['ou', 'ow'], type: 'spelling' },
    { key: 'ie_ei', letters: ['ie', 'ei'], type: 'spelling' },
    { key: 'er_ar', letters: ['er', 'ar'], type: 'spelling' },
    { key: 'er_or', letters: ['er', 'or'], type: 'spelling' },
    { key: 'tion_sion', letters: ['-tion', '-sion'], type: 'spelling' },
    { key: 'ph_f', letters: ['ph', 'f'], type: 'spelling' },
    { key: 'ck_k', letters: ['ck', 'k'], type: 'spelling' },
    // Spelling rules (word-level patterns)
    { key: 'doubling', letters: ['run', 'running'], type: 'spelling' },
    { key: 'silent_e_drop', letters: ['make', 'making'], type: 'spelling' },
    { key: 'y_to_i', letters: ['happy', 'happiness'], type: 'spelling' },
    { key: 'silent_letters', letters: ['kn-', 'wr-'], type: 'spelling' },
    { key: 'double_consonant', letters: ['-cc-', '-ss-'], type: 'spelling' },
];

const TYPE_ORDER: CategoryInfo['type'][] = ['voicing', 'liquid', 'nasal', 'cross', 'vowel', 'visual', 'spelling'];

/** Parse "bat / pat, big / pig" → [["bat","pat"], ["big","pig"]] */
function parseExamplePairs(raw: string): [string, string][] {
    return raw.split(',').map(pair => {
        const parts = pair.trim().split('/').map(w => w.trim());
        return [parts[0] || '', parts[1] || ''] as [string, string];
    }).filter(([a, b]) => a && b);
}

/** Resolve audio URL from the dictionary API to an absolute URL */
function resolveAudioUrl(url: string): string {
    if (!url) return url;
    if (/\/(localhost|127\.0\.0\.1):\d+/.test(url)) {
        const path = url.replace(/^https?:\/\/(localhost|127\.0\.0\.1):\d+/, '');
        return `${API_BASE_URL}${path}`.replace(/ /g, '%20');
    }
    if (url.startsWith('/')) {
        return `${API_BASE_URL}${url}`.replace(/ /g, '%20');
    }
    return url.replace(/ /g, '%20');
}

// ─── Word pronunciation button ───

const WordButton: React.FC<{
    word: string;
    id: string;
    speakingId: string | null;
    loadingId: string | null;
    onSpeak: (word: string, id: string) => void;
}> = ({ word, id, speakingId, loadingId, onSpeak }) => {
    const isPlaying = speakingId === id;
    const isLoading = loadingId === id;

    return (
        <button
            className={`phonics-ref__word-btn ${isPlaying ? 'phonics-ref__word-btn--playing' : ''} ${isLoading ? 'phonics-ref__word-btn--loading' : ''}`}
            onClick={() => onSpeak(word, id)}
            disabled={isLoading}
            type="button"
        >
            {isLoading ? (
                <svg className="phonics-ref__spinner" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
            ) : (
                <svg className="phonics-ref__speaker-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                </svg>
            )}
            <span>{word}</span>
        </button>
    );
};

// ─── Collapsible examples sub-component ───

const ExamplesSection: React.FC<{
    categoryKey: string;
    rawExamples: string;
    label: string;
}> = ({ categoryKey, rawExamples, label }) => {
    const [open, setOpen] = useState(false);
    const [speakingId, setSpeakingId] = useState<string | null>(null);
    const [loadingId, setLoadingId] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const pairs = parseExamplePairs(rawExamples);

    const handleSpeak = useCallback(async (word: string, id: string) => {
        // Stop any currently playing audio
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        setSpeakingId(null);
        setLoadingId(id);

        try {
            const result = await dictionaryService.lookup(word, { generateAudio: true });
            const audioUrl = result.audio?.[0]?.url;
            if (!audioUrl) {
                setLoadingId(null);
                return;
            }

            const audio = new Audio(resolveAudioUrl(audioUrl));
            audioRef.current = audio;
            setLoadingId(null);
            setSpeakingId(id);

            audio.onended = () => setSpeakingId(null);
            audio.onerror = () => setSpeakingId(null);
            await audio.play();
        } catch {
            setLoadingId(null);
            setSpeakingId(null);
        }
    }, []);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    return (
        <div className="phonics-ref__examples-section">
            <button
                className={`phonics-ref__examples-toggle ${open ? 'phonics-ref__examples-toggle--open' : ''}`}
                onClick={() => setOpen(prev => !prev)}
                type="button"
            >
                <svg className="phonics-ref__examples-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M6 9l6 6 6-6" />
                </svg>
                <span>{label}</span>
                <span className="phonics-ref__examples-count">{pairs.length}</span>
            </button>

            {open && (
                <div className="phonics-ref__examples-body">
                    {pairs.map(([wordA, wordB], idx) => {
                        const idA = `${categoryKey}-${idx}-a`;
                        const idB = `${categoryKey}-${idx}-b`;
                        return (
                            <div key={idx} className="phonics-ref__word-pair">
                                <WordButton word={wordA} id={idA} speakingId={speakingId} loadingId={loadingId} onSpeak={handleSpeak} />
                                <span className="phonics-ref__pair-slash">/</span>
                                <WordButton word={wordB} id={idB} speakingId={speakingId} loadingId={loadingId} onSpeak={handleSpeak} />
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

// ─── Main page ───

const PhonicsReferencePage: React.FC = () => {
    const { t } = useTranslation('phonicsRef');
    const location = useLocation();

    // Scroll to anchor on load
    useEffect(() => {
        if (location.hash) {
            const el = document.getElementById(location.hash.slice(1));
            if (el) {
                setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
            }
        }
    }, [location.hash]);

    const grouped = TYPE_ORDER.map(type => ({
        type,
        items: CATEGORIES.filter(c => c.type === type),
    })).filter(g => g.items.length > 0);

    return (
        <div className="phonics-ref">
            <div className="phonics-ref__header">
                <Link to="/knowledge-graph" className="phonics-ref__back">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M19 12H5" /><path d="M12 19l-7-7 7-7" />
                    </svg>
                    {t('backToGraph', 'Knowledge Graph')}
                </Link>
                <h1 className="phonics-ref__title">{t('title', 'Phonics Confusion Guide')}</h1>
                <p className="phonics-ref__subtitle">{t('subtitle', 'Understanding common letter confusions and how to overcome them')}</p>
            </div>

            {/* Intro */}
            <div className="phonics-ref__intro">
                <p>{t('intro', 'When learning English through listening and spelling, certain letter pairs are commonly confused. This guide explains why each confusion happens and provides tips to distinguish them.')}</p>
            </div>

            {/* Table of contents */}
            <nav className="phonics-ref__toc">
                <h2 className="phonics-ref__toc-title">{t('toc', 'Categories')}</h2>
                <div className="phonics-ref__toc-grid">
                    {grouped.map(g => (
                        <div key={g.type} className="phonics-ref__toc-group">
                            <span className="phonics-ref__toc-type">{t(`type.${g.type}`)}</span>
                            <div className="phonics-ref__toc-links">
                                {g.items.map(item => (
                                    <a key={item.key} href={`#${item.key}`} className="phonics-ref__toc-link">
                                        {item.letters[0]} / {item.letters[1]}
                                    </a>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </nav>

            {/* Category sections */}
            {grouped.map(g => (
                <div key={g.type} className="phonics-ref__group">
                    <h2 className="phonics-ref__group-title">{t(`type.${g.type}`)}</h2>
                    {g.items.map(item => {
                        const ns = item.type === 'spelling' ? 'digraphs' : 'cats';
                        return (
                            <div key={item.key} id={item.key} className="phonics-ref__card">
                                <div className="phonics-ref__card-header">
                                    <span className="phonics-ref__pair">
                                        <span className={`phonics-ref__letter ${item.type === 'spelling' ? 'phonics-ref__letter--digraph' : ''}`}>{item.letters[0]}</span>
                                        <span className="phonics-ref__vs">vs</span>
                                        <span className={`phonics-ref__letter ${item.type === 'spelling' ? 'phonics-ref__letter--digraph' : ''}`}>{item.letters[1]}</span>
                                    </span>
                                    <span className={`phonics-ref__type-badge phonics-ref__type-badge--${item.type}`}>
                                        {t(`type.${item.type}`)}
                                    </span>
                                </div>

                                <div className="phonics-ref__card-body">
                                    <div className="phonics-ref__section">
                                        <h4>{t('whyConfused', 'Why confused?')}</h4>
                                        <p>{t(`${ns}.${item.key}.why`)}</p>
                                    </div>
                                    <div className="phonics-ref__section">
                                        <h4>{t('howToDistinguish', 'How to distinguish')}</h4>
                                        <p>{t(`${ns}.${item.key}.tip`)}</p>
                                    </div>

                                    <ExamplesSection
                                        categoryKey={item.key}
                                        rawExamples={t(`${ns}.${item.key}.examples`)}
                                        label={t('examples', 'Examples')}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default PhonicsReferencePage;
